import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { pascalCase } from 'pascal-case';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { loadingFragments } from '../../../store/global-slice';
import { REPORTING_VAAD_LIZ_REPORTS } from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';
import { FormMode } from '../../../enums/form-mode';
import { WorkOverviewItem } from './interfaces/work-overview-item';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../components/common/tabs/tab-container';
import Title from '../../../components/common/title';
import {
  ButtonComponent,
  ButtonStyling,
} from '../../../components/form/elements/button';
import Notice from '../../../components/common/notice';
import Notices from '../../../components/common/notices';
import StepLadFields from './step-lad-fields';
import StepAalResources from './step-aal-resources';
import StepMlResources from './step-ml-resources';
import StepLimeResources from './step-lime-resources';
import StepSeedResources from './step-seed-resources';
import StepWorkOverview from './step-work-overview';
import StepEquipment from './step-equipment';
import formCss from '../../../components/form/form.module.css';
import css from './index.module.css';

enum TabsKeys {
  lad_fields = 'lad-fields',
  work_overview = 'work-overview',
  seed_resources = 'seed-resources',
  aal_resources = 'aal-resources',
  ml_resources = 'ml-resources',
  lime_resources = 'lime-resources',
  equipment = 'equipment',
}

export interface StepProps {
  mode?: FormMode;
  setIsChildLoading?: Dispatch<SetStateAction<boolean>>;
}

interface Props {
  mode?: FormMode;
}

export default function CreateReport({ mode = FormMode.create }: Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.vaad_liz_reports_v2.';
  const tp = useCallback(
    (key: string, params = null) => t(transKeyPrefix.concat(key), params),
    [t]
  );
  const { farmId, year, id } = useParams<{
    farmId: string;
    year: string;
    id: string;
  }>();
  const { vaadLizReportV2Service: vaadLizReportService } = useService();
  const [currentTab, setTab] = useState(TabsKeys.lad_fields);
  const [schema, setSchema] = useState(yup.object().shape({}));
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isChildLoading, setIsChildLoading] = useState(false);
  const [showErrorNotice, setShowErrorNotice] = useState(false);
  const formMethods = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // todo: delete after debug
  const {
    handleSubmit,
    // reset,
    trigger,
    // setError,
    getValues,
    formState: { errors, isValid },
    setValue,
  } = formMethods;
  if (process.env.NODE_ENV === 'development') {
    (window as any).errors = errors;
    (window as any).isValid = isValid;
    (window as any).getValues = getValues;
    (window as any).trigger = trigger;
    (window as any).setValue = setValue;
  }
  // todo: delete after debug

  function transformFormData(formData: WorkOverviewItem[]): {
    works: WorkOverviewItem[];
  } {
    const result = formData.map((work: WorkOverviewItem) => {
      const {
        id,
        work_id,
        treated_area: processed_area,
        dose,
        usage_reason,
        recommended_dose,
        service_provider,
        notes,
      } = work;
      return {
        id,
        work_id,
        processed_area,
        dose,
        usage_reason,
        recommended_dose,
        service_provider,
        notes,
      };
    });
    return { works: result };
  }

  const handleServiceError = useCallback(() => {
    setShowErrorNotice(true);
    dispatch(loadingFragments({ 'create-vaad-liz-report': false }));
    setIsLoadingData(false);
  }, [dispatch]);

  const step = useCallback(
    (onSuccessCallback: () => void) => {
      const formData = getValues();
      setIsLoadingData(true);
      dispatch(loadingFragments({ 'create-vaad-liz-report': true }));

      if (mode === FormMode.view) {
        return onSuccessCallback();
      }

      switch (currentTab) {
        case TabsKeys.lad_fields:
          onSuccessCallback();
          break;
        case TabsKeys.work_overview:
          // todo: trigger() to validate form
          const transformedData = transformFormData(
            Object.values(formData.work_overview)
          );
          vaadLizReportService
            .saveWorkOverview(farmId, id, transformedData)
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        case TabsKeys.seed_resources:
          vaadLizReportService
            .saveSeedResources(
              farmId,
              id,
              Object.values(formData.seed_resources)
            )
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        case TabsKeys.aal_resources:
          vaadLizReportService
            .saveAalResources(farmId, id, Object.values(formData.aal_resources))
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        case TabsKeys.ml_resources:
          vaadLizReportService
            .saveMlResources(farmId, id, {
              ml_resources: Object.values(formData.ml_resources),
              farm_n_volume: formData.farm_n_volume[0],
            })
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        case TabsKeys.lime_resources:
          vaadLizReportService
            .saveLimeResources(
              farmId,
              id,
              Object.values(formData.lime_resources)
            )
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        case TabsKeys.equipment:
          vaadLizReportService
            .saveEquipment(farmId, id, Object.values(formData.equipment))
            .subscribe(() => onSuccessCallback(), handleServiceError);
          break;
        default:
          onSuccessCallback();
          break;
      }
    },
    [
      currentTab,
      dispatch,
      farmId,
      getValues,
      handleServiceError,
      id,
      mode,
      vaadLizReportService,
    ]
  );

  const getNextTab = useCallback(() => {
    switch (currentTab) {
      case TabsKeys.lad_fields:
        return TabsKeys.work_overview;
      case TabsKeys.work_overview:
        return TabsKeys.seed_resources;
      case TabsKeys.seed_resources:
        return TabsKeys.aal_resources;
      case TabsKeys.aal_resources:
        return TabsKeys.ml_resources;
      case TabsKeys.ml_resources:
        return TabsKeys.lime_resources;
      case TabsKeys.lime_resources:
        return TabsKeys.equipment;
      case TabsKeys.equipment:
        return TabsKeys.equipment;
      default:
        return TabsKeys.lad_fields;
    }
  }, [currentTab]);

  const smartSetTab = useCallback(
    (tab: TabsKeys) => {
      if (isLoadingData || isChildLoading) return;

      step(() => {
        setIsLoadingData(false);
        dispatch(loadingFragments({ 'create-vaad-liz-report': false }));
        setTab(tab);
      });
    },
    [dispatch, isChildLoading, isLoadingData, step]
  );

  const onNext = useCallback(() => {
    if (isLoadingData || isChildLoading) return;

    step(() => {
      setIsLoadingData(false);
      dispatch(loadingFragments({ 'create-vaad-liz-report': false }));
      setTab(getNextTab());
    });
  }, [dispatch, getNextTab, isChildLoading, isLoadingData, step]);

  const onCancel = () => {
    if (isLoadingData || isChildLoading) return;

    step(() => {
      setIsLoadingData(false);
      dispatch(loadingFragments({ 'create-vaad-liz-report': false }));
      const routeTo = route(REPORTING_VAAD_LIZ_REPORTS, { farmId, year });
      history.push(routeTo);
    });
  };

  function table() {
    return (
      <>
        <Notices accessor={'stepSeedForm' || pascalCase(mode) + 'Form'}>
          {showErrorNotice ? (
            <Notice
              text={t('Form data was not saved!')}
              status="error"
              onDismiss={() => setShowErrorNotice(false)}
            />
          ) : null}
        </Notices>

        <TabContainer
          selectedTabKey={currentTab}
          setTab={smartSetTab}
          tabNavigation={
            <TabNavigation theme={'pages'}>
              <TabLink tabKey={TabsKeys.lad_fields} theme={'pages'}>
                {tp('lad_fields.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.work_overview} theme={'pages'}>
                {tp('work_overview.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.seed_resources} theme={'pages'}>
                {tp('seed_resources.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.aal_resources} theme={'pages'}>
                {tp('aal_resources.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.ml_resources} theme={'pages'}>
                {tp('ml_resources.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.lime_resources} theme={'pages'}>
                {tp('lime_resources.title')}
              </TabLink>
              <TabLink tabKey={TabsKeys.equipment} theme={'pages'}>
                {tp('equipment.title')}
              </TabLink>
            </TabNavigation>
          }
          tabContents={
            <TabContents theme={'pages'}>
              <Tab tabKey={TabsKeys.lad_fields}>
                <StepLadFields
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.work_overview}>
                <StepWorkOverview
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.seed_resources}>
                <StepSeedResources
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.aal_resources}>
                <StepAalResources
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.ml_resources}>
                <StepMlResources
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.lime_resources}>
                <StepLimeResources
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
              <Tab tabKey={TabsKeys.equipment}>
                <StepEquipment
                  mode={mode}
                  setIsChildLoading={setIsChildLoading}
                />
              </Tab>
            </TabContents>
          }
        />
      </>
    );
  }

  return (
    <div>
      <Title title={tp('title')} />

      <FormProvider {...formMethods}>
        <SchemaContext.Provider value={{ schema, setSchema }}>
          <form
            className={`${formCss['form']}`}
            onSubmit={handleSubmit(onNext)}
            autoComplete="off"
          >
            <div className={formCss['header']}>
              <h2 className={css['form-header']}>
                {tp('form.title', { year })}
              </h2>
            </div>

            <div className={formCss['body']}>{table()}</div>

            <div className={`${formCss['footer']} ${css['form-footer']}`}>
              <ButtonComponent
                name="next"
                buttonLoadingFragment="create-vaad-liz-report"
                disabled={isLoadingData || isChildLoading}
                click={onNext}
                label={tp('form.save')}
              />

              <ButtonComponent
                name="cancel"
                click={onCancel}
                disabled={isLoadingData || isChildLoading}
                label={tp('form.cancel')}
                styling={ButtonStyling.cancel}
              />
            </div>
          </form>
        </SchemaContext.Provider>
      </FormProvider>
    </div>
  );
}

export const SchemaContext = createContext({ schema: null, setSchema: null });
